import { isEmpty, isNone, isPresent } from '@ember/utils';
import EmObject, { action, computed, defineProperty, get } from '@ember/object';
import { reject, resolve } from 'rsvp';

interface ValidateAttributeOptions {
  validateDependent?: boolean;
  parents?: string[];
}

// Validator function returns error message string (invalid) or "true" (valid)
type ValidatorFunction = (val: any, model?: Record<string, any>) => string | true;

// Validator object contains validator function, dependent keys array
interface ValidatorObject {
  dependentKeys: string[];
  validator: ValidatorFunction;
}

// Validations object: public API, can have validator in both object and function form
export interface ValidationsObject {
  [field: string]: ValidatorObject | ValidatorFunction;
}

// Normalized validations object: used internally, exposes validators in unified object form only
interface NormalizedValidationsObject {
  [field: string]: ValidatorObject;
}

export default class FormValidations extends EmObject {
  isTouched = false;

  clientErrors: { [key: string]: string[] } = {};
  errors!: { [key: string]: string[] };

  // passed in .create(...) call:
  validations!: ValidationsObject;
  context: any;
  modelKey = 'model';

  @computed('validations')
  get normalizedValidations(): NormalizedValidationsObject {
    return Object.entries(this.validations).reduce(
      (normalizedValidations: NormalizedValidationsObject, [field, validation]) => {
        if (typeof validation === 'function') {
          normalizedValidations[field] = { dependentKeys: [], validator: validation as ValidatorFunction };
        } else {
          normalizedValidations[field] = validation as ValidatorObject;
        }

        return normalizedValidations;
      },
      {},
    );
  }

  init() {
    super.init();

    defineProperty(
      this,
      'errors',
      computed(`context.${this.modelKey}.errors.[]`, 'clientErrorsArray', function () {
        let { clientErrorsArray } = this;
        let model = get(this.context, this.modelKey);

        let modelErrors = model?.errors;
        let modelErrorsArray: [{ attribute: string; message: string }] = modelErrors ? modelErrors.toArray() : [];

        let errors: { [key: string]: string[] } = {};

        [...modelErrorsArray, ...clientErrorsArray].forEach((error: { attribute: string; message: string }) => {
          let { attribute, message } = error;

          if (isNone(errors[attribute])) {
            errors[attribute] = [];
          }

          errors[attribute].push(message);
        });

        return errors;
      }),
    );
  }

  @computed('clientErrors')
  get clientErrorsArray() {
    let { clientErrors } = this;
    return Object.keys(clientErrors).map((key) => ({
      attribute: key,
      message: clientErrors[key].join(' '),
    }));
  }

  @computed('clientErrors')
  get hasClientErrors() {
    return Object.values(this.clientErrors).some((error) => !isEmpty(error));
  }

  @computed('errors')
  get errorsList() {
    return Object.values(this.errors);
  }

  @computed('errors')
  get hasErrors() {
    return Object.values(this.errors).some((error) => !isEmpty(error));
  }

  addError(attribute: string, message: string) {
    let errors: { [key: string]: string[] } = {
      ...this.clientErrors,
    };
    let attributeErrors: string[] = errors[attribute];
    if (isNone(attributeErrors)) {
      attributeErrors = [];
    }
    attributeErrors.push(message);
    errors[attribute] = attributeErrors;
    this.set('clientErrors', errors);
  }

  clearAttributeErrors(attribute: string) {
    let errors: { [key: string]: string[] } = {
      ...this.clientErrors,
    };
    delete errors[attribute];
    this.set('clientErrors', errors);
  }

  clearErrors() {
    this.set('clientErrors', {});
  }

  /**
    Method calling the validator for a given attribute.
    @method validateAttribute
    @param {string} attribute - The attribute to validate
    @param {Object} options - Additional options to be used when validating
    @param {boolean} options.validateDependent - Controls whether validators
      containing the current attribute in their dependentKeys should run.
    @param {Array} options.parents - Passed into subsequent validations for
      dependentKeys, contains a list of attributes that have been validated
      before in the same chain. This is to avoid infinite validation loops
      for circular dependencies.
    @public
  */
  @action
  validateAttribute(attribute: string, options: ValidateAttributeOptions = { validateDependent: true, parents: [] }) {
    this.set('isTouched', true);
    this.clearAttributeErrors(attribute);

    if (isPresent(this.normalizedValidations[attribute])) {
      let model = get(this.context, this.modelKey);
      let value = get(model, attribute);
      let validator = this.normalizedValidations[attribute];
      let validation = validator.validator.bind(this.context)(value, model);

      if (typeof validation === 'string') {
        this.addError(attribute, validation);
      }

      if (options.validateDependent) {
        let parents = isNone(options.parents) ? [] : options.parents;
        parents.push(attribute);

        // Find validators that depend on the attribute currently being
        // validated
        let dependentValidators = Object.keys(this.normalizedValidations).filter((validatorAttribute) => {
          // Don't validate current attribute again
          if (validatorAttribute === attribute) {
            return false;
          }

          // Don't validate again if this attribute has been already validated
          // during the current validation chain
          if (parents.includes(validatorAttribute)) {
            return false;
          }

          let dependentValidator = this.normalizedValidations[validatorAttribute];
          return dependentValidator.dependentKeys?.includes(attribute) ?? false;
        });

        dependentValidators.forEach((attr) => this.validateAttribute(attr, { parents, validateDependent: true }));
      }

      return validation;
    }

    return true;
  }

  validateModel() {
    this.clearErrors();
    let validations = Object.keys(this.normalizedValidations).map((attribute) =>
      this.validateAttribute(attribute, { validateDependent: false }),
    );
    let isValid = validations.every((validation) => validation && typeof validation !== 'string');
    return isValid ? resolve(isValid) : reject(isValid);
  }

  reset() {
    this.set('isTouched', false);
    this.clearErrors();
  }
}
